(function () {
  /*
    KingCave's one comparison table (JB, 9 Oct 2026: "why would we create 2
    separate features?"). Markup: snippets/kc-compare-table.liquid. Three modes,
    set by the JSON island the snippet prints:

      pdp     a fridge page. This fridge is pinned first; the next columns are
              the visitor's Compare list (kc_compare, owned by wishlist.js), up
              to five; on a first visit the suggested fridges (kc_compare.similar,
              picked nightly) fill the table. The snippet server-renders that
              first paint so crawlers read it.
      list    /pages/wishlist#compare-root. The visitor's whole Compare list, up
              to ten, nothing pinned.
      shared  /pages/wishlist?s=SKU,SKU (or a legacy ?handles= link) - a link
              someone sent. Read-only: no add, no remove; wishlist.js's card
              grid above edits the URL list and fires wishlist:shared-changed.
              Tokens may be SKUs or handles; KCWishlist.fetchProduct resolves
              either, so `known` is keyed by whichever token the list holds.

    Everywhere: Add a fridge by model or brand (fridges only, saved fridges
    first), remove with Undo, only differences (remembered), best in row, a
    "Best for" line per fridge, "Will it fit" against the visitor's own gap
    (remembered), Add to cart in the header, share as a short ?s= link.

    Redesign, JB 14 Oct 2026 (mockup: the "Compare table redesign" canvas):
    dropped price + 5 years' power, install type and arrival time; "Cools in
    temps up to" for the ambient rating; 316 marine stainless gets its own panel
    linking the stainless guide. Share opens a panel by the button, because the
    old bottom-of-screen toast sat under Shopify's preview bar and far from the
    click.

    Product data: the page's island where it has it, otherwise
    /products/{handle}?view=compare-specs through KCWishlist.fetchProduct
    (cached). Values are shown as stored; the only arithmetic is the fit check
    and the "Best for" comparisons, both against the fridges in the table.

    Kept from the table this replaced: a column needs a fridge with specs
    (accessories inherit their parent fridge's kc_specs, so is_fridge is checked
    as well as specs); a shared link shows from one fridge, your own list says
    what to do with fewer than two; a swipe hint when columns run off a phone.
  */

  var CAP = { pdp: 6, list: 10, shared: 10 };
  var DIFF_KEY = 'kc_pcmp_diff';
  var SPACE_KEY = 'kc_pcmp_space';
  var STAINLESS_GUIDE = '/pages/best-stainless-steel-bar-fridges';
  var SAVED_FETCH_CAP = 12;

  function num(v) {
    if (v == null || v === '') return null;
    if (typeof v === 'number') return v;
    var m = String(v).match(/-?\d+(\.\d+)?/); /* first number: "2 + 1" shelves is 2, not 21 */
    return m ? parseFloat(m[0]) : null;
  }
  /* A zero here is a missing value, not a measurement (seven fridges carry a
     0kg weight). Only for fields where zero is impossible. */
  function pos(v) { var n = num(v); return n != null && n > 0 ? n : null; }
  function yes(v) { var s = String(v == null ? '' : v).toLowerCase(); return s === 'true' || s === 'yes' ? true : s === 'false' || s === 'no' ? false : null; }
  function esc(s) {
    return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
  function money(n) { return '$' + Math.round(n).toLocaleString('en-AU'); }
  function trim(n) { return String(Math.round(n * 10) / 10); }
  function dims(w, d, h) { return w != null && d != null && h != null ? w + ' x ' + d + ' x ' + h + 'mm' : null; }

  var BUILD = { ss316_marine: '316 marine stainless', ss304: '304 stainless', ss304_black: '304 stainless, black' };
  var COLOUR = { white: 'White', black: 'Black', stainless: 'Stainless', branded: 'Branded' };
  var MOUNT = { under_counter: 'under bench', built_in: 'built-in', integrated: 'integrated' };

  function price(p) { var v = num(p.price); return v == null ? null : v / 100; }
  function was(p) { var a = num(p.compare_at_price), b = num(p.price); return a != null && b != null && a > b ? a / 100 : null; }
  function S(p, k) { return p.specs ? p.specs[k] : null; }
  function hasSpecs(p) { return num(S(p, 'capacity_l')) != null || num(S(p, 'width_mm')) != null || num(S(p, 'height_mm')) != null; }
  function usable(p) { return !!p && p.is_fridge === true && hasSpecs(p); }
  function marine(p) { return S(p, 'exterior_build') === 'ss316_marine'; }
  /* Dual zone is read from the title, which JB ruled on 13 Oct is accurate for
     every dual-zone fridge. Not "beer and wine": BD425-Combo is two fridges. */
  function dualZone(p) { return /dual[\s-]zone/i.test(p.title || ''); }
  /* First "IP" followed by two digits in the certifications text. The snippet's
     first paint does the same split, so both agree. */
  function ipRating(p) {
    var c = String(S(p, 'certifications') || ''), i = c.indexOf('IP');
    if (i < 0) return null;
    var d = c.substr(i + 2, 2);
    return /^\d\d$/.test(d) ? 'IP' + d : null;
  }
  /* The gap a fridge needs: BFA's published cavity, which already includes the
     air gap. No cavity, no fit verdict - never a guess. */
  function needs(p) {
    var w = pos(S(p, 'cavity_w_mm')), d = pos(S(p, 'cavity_d_mm')), h = pos(S(p, 'cavity_h_mm'));
    return w != null && d != null && h != null ? { w: w, d: d, h: h } : null;
  }

  /* Rows. get -> comparable value (null = not listed); fmt -> text; best ->
     'low' | 'high'; kind 'cabinet' renders the 316 panel. The snippet's first
     paint mirrors this list; change both together. */
  var GROUPS = [
    { name: 'Price and running costs', rows: [
      { label: 'Price', get: price, fmt: money, best: 'low' },
      { label: 'Running cost a year', get: function (p) { return num(S(p, 'running_cost')); }, fmt: money, best: 'low' },
      { label: 'Power use a day', get: function (p) { return pos(S(p, 'power_kwh')); }, fmt: function (v) { return trim(v) + ' kWh'; }, best: 'low' },
      { label: 'Energy rating', get: function (p) { return num(S(p, 'energy_rating')); }, fmt: function (v) { return trim(v) + ' stars'; }, best: 'high' }
    ] },
    { name: 'Will it fit', fit: true, rows: [
      { label: 'Outside size', hint: 'Width x depth x height', get: function (p) { return dims(pos(S(p, 'width_mm')), pos(S(p, 'depth_mm')), pos(S(p, 'height_mm'))); } },
      { label: 'Gap it needs', hint: 'Size plus ventilation', get: function (p) { var n = needs(p); return n ? dims(n.w, n.d, n.h) : null; } },
      { label: 'Ventilation', hint: 'Rear / sides / top', get: function (p) { var r = num(S(p, 'vent_rear_mm')), s = num(S(p, 'vent_sides_mm')), t = num(S(p, 'vent_top_mm')); return r != null && s != null && t != null ? r + ' / ' + s + ' / ' + t + 'mm' : null; } },
      { label: 'Weight', get: function (p) { var w = pos(S(p, 'weight')); return w == null ? null : Math.round(w); }, fmt: function (v) { return v + 'kg'; } }
    ] },
    { name: 'Outdoors', rows: [
      { label: 'Cabinet steel', kind: 'cabinet', get: function (p) { return BUILD[S(p, 'exterior_build')] || null; } },
      { label: 'Cools in temps up to', get: function (p) { var a = pos(S(p, 'ambient_max')); return a == null ? null : Math.round(a); }, fmt: function (v) { return v + '°C'; }, best: 'high' },
      { label: 'Where it can go', get: function (p) { var l = S(p, 'location'); return l === 'both' ? 'Indoor and outdoor' : l === 'indoor' ? 'Indoor only' : null; } },
      { label: 'Heated glass', hint: 'Stops fogging in humid air', get: function (p) { var h = S(p, 'heated'), d = S(p, 'door_type'); return h === true ? 'Yes' : d === 'solid' ? 'Solid door' : d === 'open' ? 'Open front' : h === false ? 'No' : null; } },
      { label: 'Low-E glass', get: function (p) { var l = yes(S(p, 'low_e_glass')); return l === true ? 'Yes' : l === false ? 'No' : null; } },
      { label: 'Weather rating', get: ipRating }
    ] },
    { name: 'What it holds', rows: [
      { label: 'Zones', get: function (p) { return dualZone(p) ? 'Two' : 'One'; } },
      { label: 'Coldest setting', get: function (p) { var t = num(S(p, 'temp_min')); return t == null ? null : Math.round(t); }, fmt: function (v) { return v + '°C'; } },
      { label: 'Volume', get: function (p) { return num(S(p, 'capacity_l')); }, fmt: function (v) { return v + 'L'; }, best: 'high' },
      { label: 'Cans (375ml)', get: function (p) { return pos(S(p, 'cans')); }, best: 'high' },
      { label: 'Shelves', get: function (p) { return num(S(p, 'shelves')); } },
      { label: 'Inside size', hint: 'Width x depth x height', get: function (p) { return dims(pos(S(p, 'internal_w_mm')), pos(S(p, 'internal_d_mm')), pos(S(p, 'internal_h_mm'))); } }
    ] },
    { name: 'Build', rows: [
      { label: 'Doors', get: function (p) { return num(S(p, 'doors')); } },
      { label: 'Hinge side', get: function (p) { var h = S(p, 'hinge'); return h === 'both' ? 'Either side' : h ? h.charAt(0).toUpperCase() + h.slice(1) : null; } },
      { label: 'Lockable', get: function (p) { var l = S(p, 'lockable'); return l === true ? 'Yes' : l === false ? 'No' : null; } },
      { label: 'Lighting', get: function (p) { var l = yes(S(p, 'led_lighting')); return l === true ? 'LED' : l === false ? 'No' : null; } },
      { label: 'Noise', get: function (p) { return pos(S(p, 'noise_db')); }, fmt: function (v) { return v + 'dB'; }, best: 'low' },
      { label: 'Warranty', get: function (p) { return num(S(p, 'warranty_years')); }, fmt: function (v) { return v + (v === 1 ? ' year' : ' years'); } }
    ] },
    /* kind 'doc': the value is a URL and renders as a link. kc-compare-spec-json
       only emits KC-hosted files, so no BFA URL reaches this table. */
    { name: 'Documents', rows: [
      { label: 'Brochure', kind: 'doc', get: function (p) { return doc(p, 'brochure'); } },
      { label: 'Spec sheet', hint: 'Sizes and cavity drawing', kind: 'doc', get: function (p) { return doc(p, 'spec_sheet'); } },
      { label: 'User manual', kind: 'doc', get: function (p) { return doc(p, 'manual'); } }
    ] }
  ];
  function doc(p, k) { var u = p.docs ? p.docs[k] : null; return u ? String(u) : null; }

  /* One clear winner or nothing: a tie, or a lead under 2%, marks no cell
     (three fridges at 7 stars used to all get the dot). */
  function bestIndex(vals, dir) {
    var idx = [];
    vals.forEach(function (v, i) { if (typeof v === 'number') idx.push(i); });
    if (idx.length < 2) return -1;
    idx.sort(function (a, b) { return dir === 'low' ? vals[a] - vals[b] : vals[b] - vals[a]; });
    var top = vals[idx[0]], next = vals[idx[1]];
    if (top === next) return -1;
    if (Math.abs(top - next) / Math.max(Math.abs(next), 1e-9) < 0.02) return -1;
    return idx[0];
  }

  /* The unique leader on a measure, by a margin, or -1. */
  function leader(ps, get, dir, margin) {
    var vals = ps.map(get);
    var idx = [];
    vals.forEach(function (v, i) { if (v != null) idx.push(i); });
    if (idx.length < 2) return -1;
    idx.sort(function (a, b) { return dir === 'low' ? vals[a] - vals[b] : vals[b] - vals[a]; });
    return margin(vals[idx[0]], vals[idx[1]]) ? idx[0] : -1;
  }
  function pct(p) { return function (a, b) { return Math.abs(a - b) / Math.max(Math.abs(b), 1e-9) >= p; }; }
  function abs(n) { return function (a, b) { return Math.abs(a - b) >= n; }; }

  /* "Best for": one plain line per fridge, each a fact relative to the others
     in this table. Rules run in order; a fridge takes the first that fits it,
     and every rule except 316 picks one fridge at most. No rule, no line. */
  function bestFor(ps) {
    var out = ps.map(function () { return ''; });
    if (ps.length < 2) return out;
    var hot = leader(ps, function (p) { return pos(S(p, 'ambient_max')); }, 'high', abs(3));
    var anyNot316 = ps.some(function (p) { return !marine(p); });
    ps.forEach(function (p, i) {
      if (marine(p) && anyNot316) {
        out[i] = 'Coastal and exposed spots: 316 marine stainless' +
          (i === hot ? ', cools in temps up to ' + Math.round(pos(S(p, 'ambient_max'))) + '°C' : '');
      }
    });
    var anySingle = ps.some(function (p) { return !dualZone(p); });
    ps.forEach(function (p, i) {
      if (!out[i] && dualZone(p) && anySingle) out[i] = /beer/i.test(p.title || '') ? 'Beer and wine in one fridge' : 'Two zones at two temperatures';
    });
    var rules = [
      [function (p) { return pos(S(p, 'ambient_max')); }, 'high', abs(3), function (p) { return 'Hot spots: cools in temps up to ' + Math.round(pos(S(p, 'ambient_max'))) + '°C'; }],
      [function (p) { var c = pos(S(p, 'cans')), a = price(p); return c && a ? a / c : null; }, 'low', pct(0.05), function (p) { return 'The most cans for the money: about $' + (price(p) / pos(S(p, 'cans'))).toFixed(2) + ' a can'; }],
      [function (p) { return pos(S(p, 'depth_mm')); }, 'low', abs(20), function (p) { return 'Shallow benches: only ' + pos(S(p, 'depth_mm')) + 'mm deep'; }],
      [function (p) { return num(S(p, 'running_cost')); }, 'low', pct(0.05), function (p) { return 'Cheapest to run: ' + money(num(S(p, 'running_cost'))) + ' a year'; }],
      [function (p) { return num(S(p, 'capacity_l')); }, 'high', pct(0.05), function (p) { return 'The most room inside: ' + num(S(p, 'capacity_l')) + 'L'; }],
      [price, 'low', pct(0.03), function () { return 'The lowest price here'; }],
      [function (p) { return pos(S(p, 'noise_db')); }, 'low', abs(2), function (p) { return 'The quietest: ' + pos(S(p, 'noise_db')) + 'dB'; }],
      [function (p) { return pos(S(p, 'width_mm')); }, 'low', abs(30), function (p) { return 'Tight spaces: only ' + pos(S(p, 'width_mm')) + 'mm wide'; }]
    ];
    rules.forEach(function (r) {
      var i = leader(ps, r[0], r[1], r[2]);
      if (i > -1 && !out[i]) out[i] = r[3](ps[i]);
    });
    return out;
  }

  function shortName(p) { return p.sku ? (p.vendor ? p.vendor + ' ' : '') + p.sku : p.title; }
  function describe(p) {
    var parts = [];
    var c = COLOUR[S(p, 'colour')]; if (c) parts.push(c);
    var d = S(p, 'door_type');
    if (S(p, 'heated') === true) parts.push('heated glass');
    else if (d === 'glass') parts.push('glass door');
    else if (d === 'solid') parts.push('solid door');
    else if (d === 'open') parts.push('open front');
    var m = MOUNT[S(p, 'mounting_type')]; if (m) parts.push(m);
    var s = parts.join(', ');
    return s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
  }
  /* Why a fridge is offered, against the first fridge in the table. */
  function why(p, base) {
    var a = num(S(p, 'capacity_l'));
    if (!base) return a == null ? '' : a + 'L';
    var b = num(S(base, 'capacity_l'));
    var out = a == null || b == null ? 'Similar' : Math.abs(a - b) <= 2 ? 'Same size' : (a < b ? 'Smaller, ' : 'Bigger, ') + a + 'L';
    if (S(p, 'heated') === true && S(base, 'heated') !== true) return out + ', heated glass';
    if (S(p, 'door_type') !== S(base, 'door_type') && S(p, 'door_type')) return out + ', ' + S(p, 'door_type') + ' door';
    if (S(p, 'colour') !== S(base, 'colour') && COLOUR[S(p, 'colour')]) return out + ', ' + COLOUR[S(p, 'colour')].toLowerCase();
    if (S(p, 'location') !== S(base, 'location')) return out + (S(p, 'location') === 'both' ? ', outdoor rated' : ', indoor only');
    return out;
  }

  function marinePanel() {
    return '<div class="kc-pcmp__marine"><strong>316 marine stainless</strong>' +
      '<span>The grade used on boats. Stands up to salt air and pool chlorine far better than 304.</span>' +
      '<a href="' + STAINLESS_GUIDE + '">304 vs 316, explained</a></div>';
  }

  function init() {
    var root = document.querySelector('[data-kc-pcmp]');
    var island = document.getElementById('kc-pcmp-data');
    if (!root || !island || !window.KCWishlist || root.__kcPcmp) return;
    root.__kcPcmp = true; /* once: a second init would double every listener */
    var data;
    try { data = JSON.parse(island.textContent); } catch (e) { return; }
    var W = window.KCWishlist;
    var mode = data.mode === 'pdp' ? 'pdp' : (W.getSharedList && W.getSharedList() ? 'shared' : 'list');
    var cap = CAP[mode];
    var current = mode === 'pdp' ? data.current : null;
    var suggested = (data.suggested || []).filter(function (p) { return usable(p) && (!current || p.handle !== current.handle); });
    var known = {}; /* token (handle or SKU) -> product data, or null once a fetch found nothing */
    if (current) known[current.handle] = current;
    suggested.forEach(function (p) { known[p.handle] = p; });

    var hidden = {};
    var diffOn = false;
    try { diffOn = localStorage.getItem(DIFF_KEY) === '1'; } catch (e) { /* storage blocked */ }
    var space = { w: '', d: '', h: '' };
    try { var sv = JSON.parse(localStorage.getItem(SPACE_KEY) || 'null'); if (sv) space = { w: sv.w || '', d: sv.d || '', h: sv.h || '' }; } catch (e) { /* storage blocked */ }
    var query = '';
    var lastRemoved = null;
    var wantsScroll = mode !== 'pdp' && window.location.hash === '#compare-root';

    var els = {
      title: root.querySelector('[data-pcmp-title]'),
      sub: root.querySelector('[data-pcmp-sub]'),
      diff: root.querySelector('[data-pcmp-diff]'),
      share: root.querySelector('[data-pcmp-share]'),
      pop: root.querySelector('[data-pcmp-sharepop]'),
      table: root.querySelector('[data-pcmp-table]'),
      add: root.querySelector('[data-pcmp-panel]'),
      toast: document.querySelector('[data-pcmp-toast]'),
      open: root.querySelector('[data-pcmp-open]'),
      scroll: root.querySelector('.kc-pcmp__scroll'),
      hint: root.querySelector('[data-pcmp-hint]'),
      fitSum: root.querySelector('[data-pcmp-fitsum]'),
      spaceIn: root.querySelectorAll('[data-pcmp-space]')
    };

    if (mode === 'shared') {
      /* Someone else's list: read it, do not edit it from here. */
      if (els.add) els.add.hidden = true;
      if (els.open) els.open.hidden = true;
      if (els.share) els.share.hidden = true; /* the page header has Forward */
      root.classList.add('is-readonly');
    }

    /* Phones: the Add a fridge panel opens in place above the table. */
    function setAdding(on) {
      root.classList.toggle('is-adding', on);
      if (els.open) {
        els.open.setAttribute('aria-expanded', on ? 'true' : 'false');
        els.open.lastChild.textContent = on ? ' Close' : ' Add a fridge to compare';
        els.open.firstChild.textContent = on ? '×' : '+';
      }
    }

    function listHandles() {
      if (mode === 'shared') return (W.getSharedList() || { handles: [] }).handles.slice();
      return W.getCompare().map(function (i) { return i.handle; }).filter(function (h) { return !current || h !== current.handle; });
    }

    function savedHandles() {
      if (mode === 'shared' || !W.getWishlist) return [];
      return W.getWishlist().map(function (i) { return i.handle; }).slice(0, SAVED_FETCH_CAP);
    }

    function cols() {
      var room = current ? cap - 1 : cap;
      var own = listHandles().filter(function (h) { return usable(known[h]); }).slice(0, room);
      var t = mode === 'shared' ? 'shared' : 'list';
      var out = current ? [{ p: current, t: 'this' }] : [];
      out = out.concat(own.map(function (h) { return { p: known[h], t: t }; }));
      if (mode === 'pdp' && !own.length) {
        suggested.filter(function (p) { return !hidden[p.handle]; }).slice(0, room)
          .forEach(function (p) { out.push({ p: p, t: 'sug' }); });
      }
      return out;
    }

    function tagHtml(t) {
      if (t === 'this') return '<span class="kc-pcmp__tag is-this">You&rsquo;re viewing</span>';
      if (t === 'sug') return '<span class="kc-pcmp__tag is-sug">Suggested</span>';
      if (t === 'list' && mode === 'pdp') return '<span class="kc-pcmp__tag">On your list</span>';
      return '';
    }

    function emptyMessage() {
      if (mode === 'shared') return 'The fridges in this link are no longer available.';
      return 'Nothing to compare yet. Search for a fridge to add it, or tap Compare on any fridge page.';
    }

    function spaceNums() {
      var w = pos(space.w), d = pos(space.d), h = pos(space.h);
      return w && d && h ? { w: w, d: d, h: h } : null;
    }

    function fitCell(p, gap) {
      var n = needs(p);
      if (!n) return { cls: '', html: '<span class="kc-pcmp__na">Gap not listed</span>', ok: null };
      var over = [['wide', n.w - gap.w], ['deep', n.d - gap.d], ['tall', n.h - gap.h]].filter(function (o) { return o[1] > 0; });
      if (over.length) {
        over.sort(function (a, b) { return b[1] - a[1]; });
        return { cls: 'is-nofit', html: 'Too ' + over[0][0] + ' by ' + over[0][1] + 'mm', ok: false };
      }
      return { cls: 'is-fit', html: 'Fits, ' + Math.min(gap.w - n.w, gap.d - n.d, gap.h - n.h) + 'mm to spare', ok: true };
    }

    function renderTable() {
      var cs = cols();
      var n = cs.length;
      var ps = cs.map(function (c) { return c.p; });
      els.table.style.setProperty('--cols', Math.max(n, 1));
      if (!n) {
        els.table.innerHTML = '<tbody><tr><td class="kc-pcmp__empty">' + esc(emptyMessage()) + '</td></tr></tbody>';
        renderFitSummary(null);
        return;
      }
      var corner = current ? (n - 1) + ' compared with this fridge' : n + (n === 1 ? ' fridge' : ' fridges');
      var h = '<caption class="sr-only">' + esc(current ? current.title + ' compared with ' + (n - 1) + ' other fridge' + (n === 2 ? '' : 's') : 'Spec comparison, ' + corner) + '</caption>';
      h += '<thead><tr><th scope="col" class="kc-pcmp__corner"><span>' + corner + '</span>' +
        (n > 1 ? '<small class="kc-pcmp__key"><b>Best</b> marks one clear winner in a row. Ties get nothing.</small>' : '') + '</th>';
      cs.forEach(function (c) {
        var p = c.p;
        var removable = c.t === 'list' || c.t === 'sug';
        var a = price(p), wv = was(p);
        h += '<th scope="col" class="kc-pcmp__col' + (c.t === 'this' ? ' is-this' : '') + '">' + tagHtml(c.t) +
          (p.image ? '<img class="kc-pcmp__img" src="' + esc(p.image) + '" alt="" width="72" height="72" loading="lazy">' : '') +
          (marine(p) ? '<span class="kc-pcmp__badge">316 marine grade</span>' : '') +
          (c.t === 'this' ? '<span class="kc-pcmp__name">' + esc(shortName(p)) + '</span>'
                          : '<a class="kc-pcmp__name" href="' + esc(p.url) + '" title="' + esc(p.title) + '">' + esc(shortName(p)) + '</a>') +
          '<span class="kc-pcmp__desc">' + esc(describe(p)) + '</span>' +
          (a != null ? '<span class="kc-pcmp__price">' + money(a) + (wv ? ' <s class="kc-pcmp__was">' + money(wv) + '</s>' : '') + '</span>' : '') +
          (wv ? '<span class="kc-pcmp__save">Save ' + money(wv - a) + '</span>' : '') +
          (c.t === 'this' ? ''
            : p.available && p.variant_id ? '<button type="button" class="kc-pcmp__atc" data-pcmp-atc="' + p.variant_id + '">Add to cart</button>'
            : '<span class="kc-pcmp__muted kc-pcmp__soldout">Sold out</span>') +
          (removable ? '<button type="button" class="kc-pcmp__x" data-pcmp-rm="' + esc(p.handle) + '" data-t="' + c.t + '" aria-label="Remove ' + esc(shortName(p)) + ' from the comparison">&times;</button>' : '') +
          '</th>';
      });
      h += '</tr></thead><tbody>';

      var bf = bestFor(ps);
      if (bf.some(Boolean)) {
        h += '<tr class="kc-pcmp__bf"><th scope="row">Best for</th>';
        cs.forEach(function (c, i) { h += '<td' + (c.t === 'this' ? ' class="is-this"' : '') + '>' + esc(bf[i]) + '</td>'; });
        h += '</tr>';
      }

      var gap = spaceNums();
      var fits = 0, judged = 0;
      GROUPS.forEach(function (g) {
        var rows = '', hiddenRows = 0;
        if (g.fit && gap) {
          rows += '<tr class="kc-pcmp__fitrow"><th scope="row">Your ' + gap.w + ' x ' + gap.d + ' x ' + gap.h + 'mm gap</th>';
          cs.forEach(function (c) {
            var f = fitCell(c.p, gap);
            if (f.ok !== null) { judged++; if (f.ok) fits++; }
            rows += '<td class="' + f.cls + '">' + f.html + '</td>';
          });
          rows += '</tr>';
        }
        g.rows.forEach(function (r) {
          var vals = ps.map(function (p) { return r.get(p); });
          var fmt = r.fmt || String;
          var texts = vals.map(function (v) { return v == null ? null : fmt(v); });
          var shown = texts.filter(function (t) { return t != null; });
          if (!shown.length) return;
          var same = shown.length === texts.length && shown.every(function (t) { return t === shown[0]; });
          if (diffOn && same && n > 1) { hiddenRows++; return; }
          var bi = r.best && n > 1 && !same ? bestIndex(vals, r.best) : -1;
          rows += '<tr><th scope="row">' + r.label + (r.hint ? '<small>' + r.hint + '</small>' : '') + '</th>';
          cs.forEach(function (c, i) {
            var t = texts[i];
            var cls = [];
            if (c.t === 'this') cls.push('is-this');
            if (r.kind === 'cabinet' && marine(c.p)) {
              cls.push('is-marine');
              rows += '<td class="' + cls.join(' ') + '">' + marinePanel() + '</td>';
              return;
            }
            if (r.kind === 'doc' && t != null) {
              rows += '<td' + (cls.length ? ' class="' + cls.join(' ') + '"' : '') + '><a class="kc-pcmp__doc" href="' + esc(t) + '" target="_blank" rel="noopener">Open PDF</a></td>';
              return;
            }
            rows += '<td' + (cls.length ? ' class="' + cls.join(' ') + '"' : '') + '>' +
              (t == null ? '<span class="kc-pcmp__na">Not listed</span>'
                         : '<span' + (i === bi ? ' class="is-best"' : '') + '>' + esc(t) + (i === bi ? '<span class="kc-pcmp__besttag">Best</span>' : '') + '</span>') +
              '</td>';
          });
          rows += '</tr>';
        });
        if (hiddenRows) {
          rows += '<tr class="kc-pcmp__hid"><td colspan="' + (n + 1) + '">' +
            (hiddenRows === 1 ? '1 row is the same for every fridge, so it is hidden.' : hiddenRows + ' rows are the same for every fridge, so they are hidden.') + '</td></tr>';
        }
        if (rows) h += '<tr class="kc-pcmp__grp"><th scope="colgroup" colspan="' + (n + 1) + '"><span>' + g.name + '</span></th></tr>' + rows;
      });
      h += '<tr class="kc-pcmp__foot"><th scope="row"><span class="sr-only">Links</span></th>';
      cs.forEach(function (c) {
        var p = c.p;
        if (c.t === 'this') { h += '<td class="is-this"><span class="kc-pcmp__muted">This fridge</span></td>'; return; }
        h += '<td><a class="kc-pcmp__view" href="' + esc(p.url) + '">View fridge</a>' +
          (c.t === 'sug' ? '<button type="button" class="kc-pcmp__keep" data-pcmp-add="' + esc(p.handle) + '">Add to my list</button>' : '') +
          '</td>';
      });
      h += '</tr></tbody>';
      els.table.innerHTML = h;
      renderFitSummary(gap ? { fits: fits, judged: judged, gap: gap } : null);
    }

    function renderFitSummary(r) {
      if (!els.fitSum) return;
      if (!r) { els.fitSum.textContent = ''; return; }
      if (!r.judged) { els.fitSum.textContent = 'None of these fridges lists the gap it needs.'; return; }
      els.fitSum.textContent = r.fits + ' of ' + r.judged + ' fit a ' + r.gap.w + ' x ' + r.gap.d + ' x ' + r.gap.h + 'mm gap';
    }

    function renderHeader() {
      var n = cols().length;
      if (mode === 'pdp') {
        var own = listHandles().length;
        var name = current.sku || shortName(current);
        els.title.textContent = own ? 'The ' + name + ' against your list' : 'How the ' + name + ' compares';
        els.sub.textContent = own
          ? 'This fridge stays in the first column. The others come from your compare list, and changes here update that list everywhere on the site.'
          : 'Three similar fridges, side by side. Add any fridge to start your own compare list.';
      } else if (mode === 'shared') {
        els.title.textContent = 'Compare specs';
        els.sub.textContent = 'Every spec, side by side. Add any of them to your cart from the top of its column.';
      } else {
        els.title.textContent = 'Compare specs';
        els.sub.textContent = n === 0 ? 'Your compare list is empty.'
          : n === 1 ? 'Add one more fridge to compare them side by side.'
          : n + ' fridges from your compare list. Changes here update that list everywhere on the site.';
      }
      if (els.diff) els.diff.checked = diffOn;
      if (els.share) els.share.hidden = mode === 'shared' || n < 2;
      if (els.share && els.share.hidden) closeShare(false);
    }

    function candidateHtml(p) {
      var base = cols()[0];
      return '<button type="button" class="kc-pcmp__pick" data-pcmp-add="' + esc(p.handle) + '">' +
        '<span><b>' + esc(shortName(p)) + '</b><small>' + esc(describe(p)) + '</small><span class="kc-pcmp__why">' + esc(why(p, base ? base.p : null)) + '</span></span>' +
        '<span class="kc-pcmp__pick-price">' + (price(p) != null ? money(price(p)) : '') + '</span></button>';
    }

    function inTable() { return cols().filter(function (c) { return c.t !== 'sug'; }).map(function (c) { return c.p.handle; }); }

    function renderAdd() {
      if (mode === 'shared' || !els.add) return;
      var room = current ? cap - 1 : cap;
      var full = listHandles().length >= room;
      els.add.querySelector('[data-pcmp-add-open]').hidden = full;
      els.add.querySelector('[data-pcmp-add-full]').hidden = !full;
      var msg = els.add.querySelector('[data-pcmp-full-text]');
      if (msg) {
        msg.textContent = current
          ? 'Six fridges is the most this table shows. Your full list of ' + listHandles().length + ' is on the compare page.'
          : 'Ten fridges is the most this table shows. Remove one to add another.';
      }
      var link = els.add.querySelector('[data-pcmp-full-link]');
      if (link) link.hidden = !current;
      if (full) return;
      if (!query) {
        /* Saved fridges first (JB, 14 Oct: "no way to quickly add wishlist
           items"), then the nightly suggestions. */
        var shown = cols().map(function (c) { return c.p.handle; });
        var saved = savedHandles().map(function (h) { return known[h]; })
          .filter(function (p) { return usable(p) && shown.indexOf(p.handle) < 0; }).slice(0, 4);
        var savedSet = saved.map(function (p) { return p.handle; });
        var picks = suggested.filter(function (p) { return shown.indexOf(p.handle) < 0 && savedSet.indexOf(p.handle) < 0; }).slice(0, saved.length ? 2 : 4);
        var html = '';
        if (saved.length) html += '<p class="kc-pcmp__sec">From your saved list</p>' + saved.map(candidateHtml).join('');
        if (picks.length) html += (saved.length ? '<p class="kc-pcmp__sec">Similar fridges</p>' : '') + picks.map(candidateHtml).join('');
        setResults(html);
      }
    }

    function setResults(html) { els.add.querySelector('[data-pcmp-results]').innerHTML = html; }

    var searchTimer = null, searchSeq = 0;
    function search(q) {
      var seq = ++searchSeq;
      setResults('<p class="kc-pcmp__note">Searching&hellip;</p>');
      fetch('/search/suggest.json?q=' + encodeURIComponent(q) + '&resources[type]=product&resources[limit]=10')
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (j) {
          var prods = (j && j.resources && j.resources.results && j.resources.results.products) || [];
          var skip = inTable();
          var handles = prods.map(function (p) { return p.handle; }).filter(function (h) { return skip.indexOf(h) < 0; });
          return Promise.all(handles.map(function (h) { return known[h] ? Promise.resolve(known[h]) : W.fetchProduct(h); }));
        })
        .then(function (res) {
          if (seq !== searchSeq) return;
          var fr = (res || []).filter(usable).slice(0, 4);
          fr.forEach(function (p) { known[p.handle] = p; });
          setResults(fr.length ? fr.map(candidateHtml).join('')
            : '<p class="kc-pcmp__note">No fridge matches &ldquo;' + esc(q) + '&rdquo;. Try a model number such as SK126.</p>');
        })
        .catch(function () { if (seq === searchSeq) setResults('<p class="kc-pcmp__note">Search is not available right now. Try again in a moment.</p>'); });
    }

    var toastTimer = null;
    function toast(msg, undo) {
      if (!els.toast) return;
      els.toast.querySelector('[data-pcmp-toast-msg]').textContent = msg;
      els.toast.querySelector('[data-pcmp-undo]').hidden = !undo;
      els.toast.classList.add('is-on');
      clearTimeout(toastTimer);
      toastTimer = setTimeout(function () { els.toast.classList.remove('is-on'); }, 4500);
    }

    /* Phones: say so when the columns run off the screen. Desktop: when they
       fit, let the header row stick while the rows scroll under it. */
    function updateHint() {
      if (!els.scroll) return;
      var over = els.scroll.scrollWidth > els.scroll.clientWidth + 4;
      if (els.hint) els.hint.hidden = !over;
      root.classList.toggle('is-fits', !over);
    }

    /* ── Share: a panel by the button, never a toast at the screen's edge ── */
    function shareUrl() {
      return W.buildShareUrl(cols().map(function (c) { return c.p.sku || c.p.handle; }), '', '');
    }
    function prefersNativeShare() {
      return !!navigator.share && (!window.matchMedia || window.matchMedia('(pointer: coarse)').matches);
    }
    function openShare() {
      if (!els.pop) return;
      var url = shareUrl();
      var input = els.pop.querySelector('[data-pcmp-link]');
      input.value = url;
      var mail = els.pop.querySelector('[data-pcmp-email]');
      if (mail) mail.href = 'mailto:?subject=' + encodeURIComponent('Fridge comparison from KingCave') + '&body=' + encodeURIComponent(url);
      resetCopy();
      els.pop.hidden = false;
      els.share.setAttribute('aria-expanded', 'true');
      input.focus();
      input.select();
    }
    function closeShare(refocus) {
      if (!els.pop || els.pop.hidden) return;
      els.pop.hidden = true;
      if (els.share) {
        els.share.setAttribute('aria-expanded', 'false');
        if (refocus) els.share.focus();
      }
    }
    var copyTimer = null;
    function resetCopy() {
      var b = els.pop && els.pop.querySelector('[data-pcmp-copy]');
      if (b) { b.classList.remove('is-done'); b.textContent = 'Copy link'; }
    }
    function copyLink() {
      var input = els.pop.querySelector('[data-pcmp-link]');
      var b = els.pop.querySelector('[data-pcmp-copy]');
      function done() {
        b.classList.add('is-done');
        b.innerHTML = '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" aria-hidden="true"><path d="M4 12l5 5L20 6"/></svg> Link copied';
        clearTimeout(copyTimer);
        copyTimer = setTimeout(resetCopy, 2500);
      }
      function manual() { input.focus(); input.select(); b.textContent = 'Press Ctrl+C to copy'; }
      if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(input.value).then(done, manual);
      else manual();
    }

    /* Phones: with one fridge there is nothing to compare, so the search opens
       by itself rather than waiting behind the button (JB, 11 Oct: "how am i
       meant to add a fridge to the comparison on mobile??"). Once the visitor
       closes it, it stays closed. */
    var addClosedByHand = false;
    function render() {
      renderHeader(); renderTable(); renderAdd(); updateHint();
      if (mode !== 'shared' && !addClosedByHand && cols().length < 2) setAdding(true);
      if (wantsScroll) { wantsScroll = false; root.scrollIntoView({ block: 'start' }); }
    }

    /* Make sure every listed (and saved) fridge has data, then render. */
    function refresh() {
      var need = listHandles().concat(savedHandles()).filter(function (h, i, a) { return !(h in known) && a.indexOf(h) === i; });
      if (!need.length) { render(); return; }
      Promise.all(need.map(function (h) { return W.fetchProduct(h); })).then(function (res) {
        res.forEach(function (p, i) { known[need[i]] = p || null; if (p) known[p.handle] = p; });
        render();
      });
    }

    function add(handle) {
      var p = known[handle];
      if (!p) return;
      W.addToCompare(p.handle, p.title, p.url); /* compare:changed re-renders */
      if (root.classList.contains('is-adding')) {
        setAdding(false);
        /* Show the fridge just added: it is the last column. */
        setTimeout(function () {
          if (els.scroll) els.scroll.scrollLeft = els.scroll.scrollWidth;
          root.scrollIntoView({ block: 'start' });
        }, 50);
      }
      query = '';
      var input = els.add && els.add.querySelector('input[type="search"]');
      if (input) input.value = '';
      toast('Added ' + shortName(p) + ' to your compare list', false);
    }

    root.addEventListener('click', function (e) {
      if (e.target.closest('[data-pcmp-open]')) {
        var on = !root.classList.contains('is-adding');
        if (!on) addClosedByHand = true;
        setAdding(on);
        if (on) { var q = els.add.querySelector('input[type="search"]'); if (q) q.focus(); }
        return;
      }
      var rm = e.target.closest('[data-pcmp-rm]');
      if (rm) {
        var h = rm.getAttribute('data-pcmp-rm');
        if (rm.getAttribute('data-t') === 'sug') { hidden[h] = true; render(); return; }
        lastRemoved = h;
        W.removeFromCompare(h);
        toast('Removed ' + shortName(known[h] || { title: h }), true);
        return;
      }
      var ad = e.target.closest('[data-pcmp-add]');
      if (ad) { add(ad.getAttribute('data-pcmp-add')); return; }
      var atc = e.target.closest('[data-pcmp-atc]');
      if (atc) {
        atc.disabled = true;
        atc.textContent = 'Adding…';
        fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: Number(atc.getAttribute('data-pcmp-atc')), quantity: 1 }) })
          .then(function (r) { if (!r.ok) throw new Error('add failed'); })
          .then(function () {
            var a = document.createElement('a');
            a.className = 'kc-pcmp__atc is-done';
            a.href = '/cart';
            a.textContent = 'Added. View cart';
            atc.replaceWith(a);
          })
          .catch(function () { atc.disabled = false; atc.textContent = 'Try again'; });
        return;
      }
      if (e.target.closest('[data-pcmp-share]')) {
        if (prefersNativeShare()) { navigator.share({ title: 'KingCave comparison', url: shareUrl() }).catch(function () {}); return; }
        if (els.pop && !els.pop.hidden) closeShare(true); else openShare();
        return;
      }
      if (e.target.closest('[data-pcmp-copy]')) { copyLink(); return; }
      if (e.target.closest('[data-pcmp-shareclose]')) { closeShare(true); return; }
    });

    document.addEventListener('click', function (e) {
      if (els.pop && !els.pop.hidden && !e.target.closest('[data-pcmp-sharepop]') && !e.target.closest('[data-pcmp-share]')) closeShare(false);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && els.pop && !els.pop.hidden) closeShare(true);
    });

    if (els.diff) {
      els.diff.addEventListener('change', function () {
        diffOn = els.diff.checked;
        try { localStorage.setItem(DIFF_KEY, diffOn ? '1' : '0'); } catch (e) { /* storage blocked */ }
        renderTable();
        updateHint();
      });
    }

    /* Will it fit: the visitor's gap, remembered across pages. */
    var spaceTimer = null;
    Array.prototype.forEach.call(els.spaceIn, function (inp) {
      var k = inp.getAttribute('data-pcmp-space');
      inp.value = space[k] || '';
      inp.addEventListener('input', function () {
        space[k] = inp.value.replace(/[^\d]/g, '').slice(0, 5);
        if (inp.value !== space[k]) inp.value = space[k];
        try { localStorage.setItem(SPACE_KEY, JSON.stringify(space)); } catch (e) { /* storage blocked */ }
        clearTimeout(spaceTimer);
        spaceTimer = setTimeout(function () { renderTable(); updateHint(); }, 200);
      });
    });

    var input = els.add && els.add.querySelector('input[type="search"]');
    if (input) {
      input.addEventListener('input', function () {
        query = input.value.trim();
        clearTimeout(searchTimer);
        if (query.length < 2) { query = ''; renderAdd(); return; }
        searchTimer = setTimeout(function () { search(query); }, 250);
      });
    }

    if (els.toast) {
      els.toast.querySelector('[data-pcmp-undo]').addEventListener('click', function () {
        if (lastRemoved && known[lastRemoved]) {
          var p = known[lastRemoved];
          W.addToCompare(p.handle, p.title, p.url);
        }
        lastRemoved = null;
        els.toast.classList.remove('is-on');
      });
    }

    if (mode === 'pdp') {
      /* The buy column's Compare row links "View (N)" to the compare page; with
         the table on this page, scroll to it instead. */
      document.addEventListener('click', function (e) {
        var v = e.target.closest('.kc-cmprow__view');
        if (!v) return;
        e.preventDefault();
        var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        root.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
      });
    }

    if (mode === 'shared') {
      window.addEventListener('wishlist:shared-changed', refresh);
    } else {
      window.addEventListener('compare:changed', refresh);
      window.addEventListener('wishlist:changed', refresh);
      window.addEventListener('storage', function (e) { if (e.key === 'kc_compare' || e.key === 'kc_wishlist') refresh(); });
    }
    window.addEventListener('resize', updateHint);

    refresh();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
